import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Building,
  MapPin,
  Briefcase,
  DollarSign,
  CheckCircle,
  XCircle,
  Eye,
  Gift,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { candidateJobOfferService } from '@/services/jobOfferService';
import { JobOffer } from '@/types';
import { toast } from '@/stores/toastStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { useAuth } from '@/stores/authStore';

export default function CandidateJobOfferDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['jobOffers', 'common']);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState<JobOffer | null>(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Only fetch data if user has given consent
  useEffect(() => {
    // Don't fetch offer details until user has given consent
    if (user?.dataProcessingConsent !== true) {
      console.log('[CandidateJobOfferDetails] User has not given consent - skipping data fetch');
      setLoading(false);
      return;
    }

    console.log('[CandidateJobOfferDetails] User has given consent - loading offer details...');
    loadOfferDetails();
  }, [id, user?.dataProcessingConsent]);

  const loadOfferDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await candidateJobOfferService.getJobOfferDetails(id);
      setOffer(data);
    } catch (error: any) {
      console.error('Failed to load offer details:', error);
      toast.error(t('errors.detailsFailed'));
      navigate('/candidate/job-offers');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!id) return;
    try {
      setSubmitting(true);
      await candidateJobOfferService.acceptJobOffer(id, { notes });
      toast.success(t('candidate.accept.success'));
      navigate('/candidate/job-offers');
    } catch (error: any) {
      console.error('Failed to accept offer:', error);
      toast.error(t('candidate.accept.error'));
    } finally {
      setSubmitting(false);
      setShowAcceptModal(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      setSubmitting(true);
      await candidateJobOfferService.rejectJobOffer(id, { reason, notes });
      toast.success(t('candidate.reject.success'));
      navigate('/candidate/job-offers');
    } catch (error: any) {
      console.error('Failed to reject offer:', error);
      toast.error(t('candidate.reject.error'));
    } finally {
      setSubmitting(false);
      setShowRejectModal(false);
    }
  };

  const formatSalary = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return t('candidate.details.notSpecified');
    const cur = currency || 'TND';
    if (min && max) {
      return `${min.toLocaleString()} - ${max.toLocaleString()} ${cur}`;
    }
    if (min) return `${min.toLocaleString()}+ ${cur}`;
    if (max) return `${t('candidate.details.upTo')} ${max.toLocaleString()} ${cur}`;
    return t('candidate.details.notSpecified');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canRespond = offer && (offer.status === 'PENDING' || offer.status === 'VIEWED');

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <div className="container-custom py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/candidate/job-offers')}
        className="flex items-center gap-2 text-gray-700 hover:text-blue-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">{t('candidate.details.backToList')}</span>
      </button>

      {/* Header Card */}
      <Card className="mb-6 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex-shrink-0">
              <Building className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{offer.position}</h1>
              <p className="text-xl text-gray-700 font-medium">{offer.companyName}</p>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <span
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                offer.status === 'ACCEPTED'
                  ? 'bg-green-100 text-green-700'
                  : offer.status === 'REJECTED'
                  ? 'bg-red-100 text-red-700'
                  : offer.status === 'EXPIRED'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {t(`statuses.${offer.status}`)}
            </span>
          </div>
        </div>

        {/* Key Details Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <MapPin className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">{t('candidate.details.location')}</p>
              <p className="text-sm font-semibold text-gray-900">{offer.location || t('candidate.details.notSpecified')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Briefcase className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">{t('candidate.details.employmentType')}</p>
              <p className="text-sm font-semibold text-gray-900">{t(`employmentTypes.${offer.employmentType}`)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Eye className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">{t('candidate.details.workMode')}</p>
              <p className="text-sm font-semibold text-gray-900">{t(`workModes.${offer.workMode}`)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <DollarSign className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">{t('candidate.details.salary')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {formatSalary(offer.salaryMin, offer.salaryMax, offer.salaryCurrency)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      {canRespond && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Button
            onClick={() => setShowAcceptModal(true)}
            variant="primary"
            icon={<CheckCircle className="w-5 h-5" />}
            className="flex-1"
          >
            {t('candidate.details.acceptOffer')}
          </Button>
          <Button
            onClick={() => setShowRejectModal(true)}
            variant="secondary"
            icon={<XCircle className="w-5 h-5" />}
            className="flex-1"
          >
            {t('candidate.details.rejectOffer')}
          </Button>
        </div>
      )}

      {/* Details Sections */}
      <div className="space-y-6">
        {/* Message from Recruiter */}
        {offer.message && (
          <Card className="shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {t('candidate.details.message')}
            </h2>
            <p className="text-gray-700 whitespace-pre-wrap">{offer.message}</p>
          </Card>
        )}

        {/* Responsibilities */}
        {offer.responsibilities && (
          <Card className="shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">{t('candidate.details.responsibilities')}</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{offer.responsibilities}</p>
          </Card>
        )}

        {/* Requirements */}
        {offer.requirements && (
          <Card className="shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">{t('candidate.details.requirements')}</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{offer.requirements}</p>
          </Card>
        )}

        {/* Benefits */}
        {offer.benefits && (
          <Card className="shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-600" />
              {t('candidate.details.benefits')}
            </h2>
            <p className="text-gray-700 whitespace-pre-wrap">{offer.benefits}</p>
          </Card>
        )}

        {/* Metadata */}
        <Card className="shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">{t('candidate.details.metadata')}</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">{t('candidate.details.status')}</p>
              <p className="text-gray-900 font-medium">{t(`statuses.${offer.status}`)}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">{t('candidate.details.createdAt')}</p>
              <p className="text-gray-900 font-medium">{formatDate(offer.createdAt)}</p>
            </div>
            {offer.viewedAt && (
              <div>
                <p className="text-gray-600 mb-1">{t('candidate.details.viewedAt')}</p>
                <p className="text-gray-900 font-medium">{formatDate(offer.viewedAt)}</p>
              </div>
            )}
            {offer.respondedAt && (
              <div>
                <p className="text-gray-600 mb-1">{t('candidate.details.respondedAt')}</p>
                <p className="text-gray-900 font-medium">{formatDate(offer.respondedAt)}</p>
              </div>
            )}
            {offer.expiresAt && (
              <div>
                <p className="text-gray-600 mb-1">{t('candidate.details.expiresAt')}</p>
                <p className="text-gray-900 font-medium">{formatDate(offer.expiresAt)}</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Accept Modal */}
      <Modal isOpen={showAcceptModal} onClose={() => setShowAcceptModal(false)} title={t('candidate.accept.title')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-900">{t('candidate.accept.message')}</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('candidate.accept.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('candidate.accept.notesPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-400"
              rows={4}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={handleAccept} variant="primary" disabled={submitting} className="flex-1">
              {submitting ? t('candidate.accept.loading') : t('candidate.accept.confirm')}
            </Button>
            <Button onClick={() => setShowAcceptModal(false)} variant="secondary" disabled={submitting} className="flex-1">
              {t('candidate.accept.cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title={t('candidate.reject.title')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900">{t('candidate.reject.message')}</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('candidate.reject.reason')}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('candidate.reject.reasonPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-400"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('candidate.reject.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('candidate.reject.notesPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-400"
              rows={2}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={handleReject} variant="danger" disabled={submitting} className="flex-1">
              {submitting ? t('candidate.reject.loading') : t('candidate.reject.confirm')}
            </Button>
            <Button onClick={() => setShowRejectModal(false)} variant="secondary" disabled={submitting} className="flex-1">
              {t('candidate.reject.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
